import { Copy, RefreshCw } from "lucide-react";
import rehypeExternalLinks from "rehype-external-links";
import { Streamdown } from "streamdown";

import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import {
  IgTypography,
  IgTypographyProps,
} from "@intelligems/ig-theme/IgTypography";
import { AiFeature } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import {
  FeedbackTarget,
  RateAi,
} from "@src/features/ai/components/usages/RateAi";

interface AnalyticsSummaryBaseProps {
  title?: string;
  summary?: string;
  isLoading?: boolean;
  errorMessage?: string;
  timestamp?: string | null;
  formatTimestamp?: (timestamp: string) => string;
  onCopy?: () => void;
  copyFeedback?: boolean;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  refreshTooltip?: string;
  titleVariant?: IgTypographyProps["variant"];
  titleWidth?: number;
  headerSlot?: React.ReactNode;
  actionSlot?: React.ReactNode;
  footerSessionId?: string;
  footerWorkflowId?: string;
  footerFeature?: AiFeature;
  footerFeedbackTarget?: FeedbackTarget;
  footerShowCommentsDialog?: boolean;
}

const escapeApproximateTildes = (summary: string) =>
  summary.replace(/(?<!~)~(?=\s*[+-]?\d)(?!~)/g, "\\~");

export const AnalyticsSummaryBase = ({
  title = "Results Summary",
  summary,
  isLoading = false,
  errorMessage,
  timestamp,
  formatTimestamp,
  onCopy,
  copyFeedback = false,
  onRefresh,
  refreshDisabled = false,
  refreshTooltip = "Regenerate summary",
  titleVariant = "h2",
  titleWidth = 300,
  headerSlot,
  actionSlot,
  footerSessionId,
  footerWorkflowId,
  footerFeature = "analytics",
  footerFeedbackTarget = "mixpanel",
  footerShowCommentsDialog = false,
}: AnalyticsSummaryBaseProps) => {
  return (
    <Grid container spacing={2}>
      <Grid
        container
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        {...(actionSlot && { size: "grow" })}
      >
        <Grid
          container
          alignItems={"center"}
          columnSpacing={2}
          flexWrap={"nowrap"}
          size="grow"
        >
          {headerSlot}
          <Grid>
            {!isLoading ? (
              <IgTypography variant={titleVariant} fontWeight={600}>
                {title}
              </IgTypography>
            ) : (
              <Box sx={{ width: titleWidth }}>
                <SkeletonGroup lines={1} />
              </Box>
            )}
          </Grid>
        </Grid>
        {actionSlot}
      </Grid>
      <Grid container size={12}>
        {!isLoading && summary ? (
          <IgTypography
            variant={"body1"}
            color={"textPrimary"}
            component="div"
            sx={{
              "& > [data-streamdown]:first-of-type": {
                marginTop: 0,
              },
              "& > [data-streamdown]:last-child": {
                marginBottom: 0,
              },
              "& ul[data-streamdown]": {
                listStyleType: "disc",
                paddingLeft: "1.5rem",
                margin: "0.5rem 0",
              },
              "& ol[data-streamdown]": {
                listStyleType: "decimal",
                paddingLeft: "1.5rem",
                margin: "0.5rem 0",
              },
              "& li[data-streamdown]": {
                padding: "0.125rem 0",
              },
            }}
          >
            <Streamdown
              rehypePlugins={[
                [
                  rehypeExternalLinks,
                  {
                    target: "_blank",
                    rel: ["nofollow", "noopener", "noreferrer"],
                  },
                ],
              ]}
            >
              {escapeApproximateTildes(summary)}
            </Streamdown>
          </IgTypography>
        ) : !isLoading && errorMessage ? (
          <IgTypography variant={"body1"} color={"textSecondary"}>
            {errorMessage}
          </IgTypography>
        ) : (
          <SkeletonGroup groupSx={{ width: "100%" }} lines={4} />
        )}
      </Grid>
      <Grid
        container
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"end"}
      >
        {timestamp && formatTimestamp ? (
          <Grid>
            <IgTypography variant={"body2"} color={"textSecondary"}>
              {formatTimestamp(timestamp)}
            </IgTypography>
          </Grid>
        ) : null}
        {summary && footerSessionId ? (
          <Stack sx={{ mr: "-8px", mb: "-4px" }}>
            <RateAi
              prependExtraButtons={
                <>
                  {onCopy ? (
                    <IgTooltip
                      title={copyFeedback ? "Copied!" : "Copy summary"}
                    >
                      <IgIconButton
                        size={"small"}
                        onClick={onCopy}
                        disabled={!summary}
                      >
                        <Copy size={14} />
                      </IgIconButton>
                    </IgTooltip>
                  ) : null}
                  {onRefresh ? (
                    <IgTooltip title={refreshTooltip}>
                      <IgIconButton
                        size={"small"}
                        onClick={onRefresh}
                        disabled={refreshDisabled}
                      >
                        <RefreshCw size={14} />
                      </IgIconButton>
                    </IgTooltip>
                  ) : null}
                </>
              }
              sessionId={footerSessionId}
              workflowId={footerWorkflowId}
              feature={footerFeature}
              feedbackTarget={footerFeedbackTarget}
              showCommentsDialog={footerShowCommentsDialog}
            />
          </Stack>
        ) : null}
      </Grid>
    </Grid>
  );
};
